/**
 * Experience Workbook — enums, labels and lookups shared by the workbench, the
 * item dialog and the grid. Values are VERBATIM from the backend models
 * (`ValidatedExperience` options, `EXPERIENCE_ITEM_SOURCES`) — labels may be
 * prettier, values may not.
 */
import { formatAddressObject } from "../../../_src_shared/utils/functions/formatAddressObject";

/** `ValidatedExperience.experienceType` options (pipeline.py). */
export const EXPERIENCE_TYPES = [
  "Fix and Flip",
  "Ground Up Construction",
  "Rental",
  "Multifamily",
  "Commercial",
  "Other",
];

/**
 * `PRODUCT_FAMILY_OF` + `product_family()` from `app/models/pipeline.py`, and
 * `EXPERIENCE_PRODUCT_FAMILIES` / `experience_applies()` from
 * `app/models/experience.py` — the product rule that decides whether the
 * workbook applies at all (RTL family + Ground Up Construction; DSCR and Other
 * get no Experience tab).
 *
 * This exists because `/experience/*` still resolves the deal out of
 * `underwriting_pipeline` ALONE, so an acquisition's `experienceApplies` comes
 * back false for "deal not found" rather than "wrong product". Deriving the
 * same answer client-side lets the postclose tab appear for exactly the
 * products the preclose tab does. Delete this the day the router adopts the
 * dual-pipeline `_resolve_deal` that checklists.py / loan_files.py use.
 */
const PRODUCT_FAMILY_OF = {
  "Fix and Flip": "fix_and_flip",
  "Ground Up Construction": "ground_up",
  "Stabilized Bridge": "bridge",
  DSCR: "dscr",
  Other: "other",
  "Multifamily Bridge": "multifamily",
  "Multifamily Value Add": "multifamily",
  "HVRTL - Fix and Flip": "hvrtl",
  "HVRTL - Bridge": "hvrtl",
};

const EXPERIENCE_PRODUCT_FAMILIES = new Set([
  "fix_and_flip",
  "bridge",
  "ground_up",
  "multifamily",
]);

/** loanProductType → product family, or null when empty. Exact option match
 *  first (then case-insensitive); the substring fallbacks cover the strings
 *  that are NOT options — Salesforce labels, sizer shorthands, rate-sheet
 *  labels — exactly as the backend orders them. */
export const productFamily = (loanProductType) => {
  if (!loanProductType) return null;
  const s = String(loanProductType).trim();
  if (PRODUCT_FAMILY_OF[s]) return PRODUCT_FAMILY_OF[s];
  const low = s.toLowerCase();
  const hit = Object.entries(PRODUCT_FAMILY_OF).find(
    ([k]) => k.toLowerCase() === low,
  );
  if (hit) return hit[1];
  if (low.includes("dscr") || low.includes("rental")) return "dscr";
  if (low.includes("ground") || low === "guc" || low === "gu") return "ground_up";
  if (low.includes("hvrtl") || low.includes("high value")) return "hvrtl";
  if (low.includes("multifamily") || low.includes("multi-family") || low.includes("5+"))
    return "multifamily";
  if (low.includes("bridge")) return "bridge";
  if ((low.includes("fix") && low.includes("flip")) || low.includes("rehab"))
    return "fix_and_flip";
  return "other";
};

/** `experience_applies()` — true when the workbook applies to this product.
 *  An unknown/empty product type is false, same as the backend. */
export const experienceAppliesToProduct = (loanProductType) =>
  EXPERIENCE_PRODUCT_FAMILIES.has(productFamily(loanProductType));

/** `ValidatedExperience.resolutionStatus` options (pipeline.py). */
export const RESOLUTION_STATUSES = ["Sold", "Retained as Rental", "In Progress"];

/**
 * `ExperienceItem.qualifyingCategory` — the guideline bucket that decides how
 * much an item is worth (owner 2026-09-22).
 *
 * Values are VERBATIM from `QUALIFYING_CATEGORY_SPEC` in
 * `app/models/experience.py`; the points mirror that same map. An income-
 * producing purchase and third-party GC work count HALF, everything else a full
 * point. There is no `/model_schemas` object type for an experience item, so
 * this constant IS the list — keep it in step with the model, never edit a
 * value to read more nicely.
 *
 * An UNCLASSIFIED item counts 1.0 until someone classifies it, which is why a
 * verified row with no category is worth flagging rather than ignoring.
 */
export const QUALIFYING_CATEGORY_POINTS = {
  "Fix and Flip - Sold": 1.0,
  "Fix and Flip - Retained as Rental": 1.0,
  "Income Producing Property Purchased": 0.5,
  "Ground Up - Completed and Sold": 1.0,
  "Ground Up - Retained as Rental": 1.0,
  "General Contractor for Third Party": 0.5,
};

export const QUALIFYING_CATEGORIES = Object.keys(QUALIFYING_CATEGORY_POINTS);

/**
 * Doors one address represents (`unitCount`, 2026-10-20): a 5-unit condo
 * sell-out counts 5. Empty, 0 or 1 all mean one.
 */
export const itemUnits = (item) => {
  const n = Math.floor(Number(item?.unitCount));
  return Number.isFinite(n) && n > 1 ? n : 1;
};

/**
 * An item counting several doors with no note saying why — the backend guide
 * asks for it to be flagged (experience-workbook.md, door count + notes).
 */
export const unitsNeedNote = (item) =>
  itemUnits(item) > 1 && !String(item?.experienceNotes || "").trim();

/**
 * The points an item contributes — the server's `qualifyingPoints` (already
 * category points × units), or the same math locally.
 */
export const itemPoints = (item) => {
  const served = Number(item?.qualifyingPoints);
  if (Number.isFinite(served)) return served;
  const fromCategory = QUALIFYING_CATEGORY_POINTS[item?.qualifyingCategory];
  return (Number.isFinite(fromCategory) ? fromCategory : 1.0) * itemUnits(item);
};

/** A verified row nobody has classified yet — it is counting 1.0 on trust. */
export const missingCategory = (item) =>
  !!item?.verified && !item?.qualifyingCategory;

/**
 * Experience totals are POINTS, not a count of rows — always one decimal, so
 * `2.5` and `2.0` read as the same kind of number and a half point is never
 * mistaken for a rounding artifact (experience-workbook.md §6).
 */
export const fmtPoints = (v) => {
  const n = Number(v ?? 0);
  return (Number.isFinite(n) ? n : 0).toFixed(1);
};

/** `ExperienceItem.source` — where a row came from. */
export const SOURCES = {
  track_record: { label: "Track record", hint: "Seeded from a parsed track-record schedule" },
  borrower_claim: { label: "Borrower claim", hint: "Stated by the borrower, not yet evidenced" },
  manual: { label: "Manual", hint: "Entered by the reviewer" },
  portable: { label: "Portable", hint: "Carried in from the shared canonical record" },
};

/** Association types the endpoint accepts (anything else → 400). */
export const ASSOCIATION_TYPES = {
  BORROWERS: "borrowers",
  ENTITIES: "entities",
  GENERAL_CONTRACTORS: "generalContractors",
  LOAN_DOCUMENTS: "loanDocuments",
  SUBJECT_PROPERTIES: "subjectProperties",
};

/**
 * Proof-document picker: the Experience and Track Record group sorts first
 * (docTypes moved there 2026-09-15), then anything matching the proof hints
 * (HUD / settlement / deed / property record / recorded), then the rest.
 */
export const EXPERIENCE_DOC_GROUP = "Experience and Track Record";
export const CONTRACTOR_DOC_GROUP = "General Contractor Docs";
const PROOF_HINT = /experience|hud|settlement|closing statement|deed|property record|property report|recorded|payoff|title/i;

export const proofRank = (doc) => {
  if (doc?.docGroup === EXPERIENCE_DOC_GROUP) return 0;
  if (/^Contractor Experience/i.test(doc?.docType || "")) return 0;
  if (PROOF_HINT.test(`${doc?.docType || ""} ${doc?.docGroup || ""}`)) return 1;
  return 2;
};

export const sortProofDocs = (docs) =>
  [...(docs || [])].sort((a, b) => {
    const r = proofRank(a) - proofRank(b);
    if (r !== 0) return r;
    return String(docName(a)).localeCompare(String(docName(b)));
  });

export const docName = (d) => d?.file_display_name || d?.fileName || d?.docType || "Document";

/* ─────────────────────────── party lookups ─────────────────────────────── */

export const personName = (p) =>
  [p?.firstName, p?.lastName].filter(Boolean).join(" ") || p?.emailAddress || "Unnamed";
export const entityName = (e) => e?.entityName || "Unnamed entity";
export const gcName = (g) =>
  g?.company || [g?.firstName, g?.lastName].filter(Boolean).join(" ") || "Unnamed contractor";

/**
 * Every party on the loan that can PARENT an experience item, keyed by id:
 * `{ id, type, name, verifiedExperienceTotal, raw }` where `type` is the
 * association type. `verifiedExperienceTotal` is the loan's rollup field for
 * that party — always written after every mutation, never stale.
 */
export const listLoanParties = (loan) => {
  const out = [];
  (loan?.borrowers || []).forEach((b) =>
    out.push({
      id: String(b?._id),
      type: ASSOCIATION_TYPES.BORROWERS,
      name: personName(b),
      verifiedExperienceTotal: b?.verifiedExperienceTotal ?? 0,
      raw: b,
    }),
  );
  (loan?.entities || []).forEach((e) =>
    out.push({
      id: String(e?._id),
      type: ASSOCIATION_TYPES.ENTITIES,
      name: entityName(e),
      verifiedExperienceTotal: e?.verifiedExperienceTotal ?? 0,
      raw: e,
    }),
  );
  (loan?.generalContractors || []).forEach((g) =>
    out.push({
      id: String(g?._id),
      type: ASSOCIATION_TYPES.GENERAL_CONTRACTORS,
      name: gcName(g),
      verifiedExperienceTotal: g?.verifiedExperienceTotal ?? 0,
      raw: g,
    }),
  );
  return out;
};

export const partiesById = (loan) => new Map(listLoanParties(loan).map((p) => [p.id, p]));

/** The item's parents, resolved: `[{id, type, name}]`. */
export const itemParents = (item, byId) => {
  const a = item?.associations || {};
  return [
    ...(a.borrowers || []).map((id) => ({ id: String(id), type: ASSOCIATION_TYPES.BORROWERS })),
    ...(a.entities || []).map((id) => ({ id: String(id), type: ASSOCIATION_TYPES.ENTITIES })),
    ...(a.generalContractors || []).map((id) => ({
      id: String(id),
      type: ASSOCIATION_TYPES.GENERAL_CONTRACTORS,
    })),
  ].map((p) => ({ ...p, name: byId?.get(p.id)?.name || `…${p.id.slice(-5)}` }));
};

export const hasParent = (item) => itemParents(item).length > 0;

export const itemCreditsParty = (item, partyId) =>
  itemParents(item).some((p) => p.id === String(partyId));

/** Only verified AND counted rows count — mirror the backend's rollup math. */
export const itemCounts = (item) => !!item?.verified && item?.countedForExperience !== false;

export const itemAddress = (item) => {
  const a = item?.propertyAddress;
  if (!a) return "—";
  const s = formatAddressObject(a);
  return s === "N/A" ? "—" : s;
};

/** Proof attached? (`associations.loanDocuments` non-empty). */
export const hasProof = (item) => (item?.associations?.loanDocuments || []).length > 0;

/**
 * Needs attention before it can be verified: no parent (persists nowhere) or
 * no proof (unevidenced). The amber rail on the ledger and the filter chip.
 */
export const needsAttention = (item) => !hasParent(item) || !hasProof(item);
